import DOMPurify from 'dompurify';

// Open links in a new tab and harden against reverse-tabnabbing.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

// Everything the rich-text editors can produce. Explicit rather than a broad
// profile so viewer and editor agree on exactly which markup survives.
const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup',
  'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'pre', 'code',
  'a',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'figure', 'figcaption',
];

// href/target/rel keep links clickable; colspan/rowspan keep editor tables intact.
const ALLOWED_ATTR = ['href', 'target', 'rel', 'title', 'colspan', 'rowspan', 'class'];

/**
 * Sanitize rich HTML (CKEditor output for comments, descriptions, pages) for
 * safe read-only rendering via dangerouslySetInnerHTML. Strips scripts, event
 * handlers and unsafe URL schemes while keeping formatting, links, lists and
 * tables intact.
 *
 * DOMPurify parses the markup rather than escaping it with regexes, so it is
 * idempotent — sanitizing twice never double-encodes "&" into "&amp;".
 */
export function sanitizeRichHtml(raw) {
  if (!raw) return '';
  return DOMPurify.sanitize(raw, { ALLOWED_TAGS, ALLOWED_ATTR });
}

// True when the HTML has no visible text/content once tags are stripped.
export const richHtmlIsEmpty = (s) =>
  !s || !s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();

// --------------------------------------------------------------- plain text

// Mirrors backend/utils/html_normalize.py. Required for rows written before
// that normalization existed, which are still stored as plain text.
// Narrow on purpose: a lone "<b>" or "a < b" is prose, not markup, so we only
// treat a value as HTML when it closes a tag or uses a void tag.
const CLOSING_TAG_RE = /<\/(?:p|ul|ol|li|strong|b|em|i|u|s|a|h[1-6]|blockquote|pre|code|table|thead|tbody|tr|td|th|div|span|figure)\s*>/i;
const VOID_TAG_RE = /<(?:br|hr|img)\b[^>]*\/?>/i;

const BULLET_RE = /^\s*[-*•]\s+(.*)$/;
const NUMBERED_RE = /^\s*\d+[.)]\s+(.*)$/;
const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]}'"])/g;

export const looksLikeHtml = (value) =>
  !!value && (CLOSING_TAG_RE.test(value) || VOID_TAG_RE.test(value));

// Escape plain text exactly once — never applied to already-encoded input.
const escapeHtml = (s) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// '&' is already '&amp;' at this point, which is the correct form inside href.
const linkify = (escaped) => escaped.replace(URL_RE, (url) => `<a href="${url}">${url}</a>`);

const inline = (line) => linkify(escapeHtml(line));

/**
 * Convert plain text to HTML: blank lines become paragraphs, single newlines
 * become <br>, and runs of "- "/"1." lines become <ul>/<ol> lists.
 */
export function plainTextToHtml(text) {
  if (!text) return '';

  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let paragraph = [];
  let listItems = [];
  let listTag = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push(`<p>${paragraph.map(inline).join('<br>')}</p>`);
      paragraph = [];
    }
  };
  const flushList = () => {
    if (listItems.length) {
      blocks.push(`<${listTag}>${listItems.map((i) => `<li>${inline(i)}</li>`).join('')}</${listTag}>`);
      listItems = [];
      listTag = null;
    }
  };

  lines.forEach((line) => {
    const bullet = BULLET_RE.exec(line);
    const numbered = bullet ? null : NUMBERED_RE.exec(line);

    if (bullet || numbered) {
      const tag = bullet ? 'ul' : 'ol';
      if (listTag && listTag !== tag) flushList();
      flushParagraph();
      listTag = tag;
      listItems.push((bullet || numbered)[1].trim());
    } else if (!line.trim()) {
      flushList();
      flushParagraph();
    } else {
      flushList();
      paragraph.push(line);
    }
  });

  flushList();
  flushParagraph();
  return blocks.join('');
}

/**
 * Normalize a stored value into the HTML the editor expects. Use this whenever
 * content is loaded into CKEditor, so legacy plain-text rows keep their
 * paragraphs and lists instead of collapsing into one block.
 */
export function toEditorHtml(value) {
  if (!value) return '';
  return looksLikeHtml(value) ? value : plainTextToHtml(value);
}

/** Normalize then sanitize — the single entry point for read-only rendering. */
export function renderRichHtml(value) {
  return sanitizeRichHtml(toEditorHtml(value));
}

export default sanitizeRichHtml;
